import { WAITLIST_QUESTIONS, WAITLIST_STEP_COUNT, emptyWaitlistAnswers } from './waitlistOptions';

/**
 * Browser-local draft of the waiting-list questionnaire.
 *
 * The four answers only reach the backend when the applicant presses Done
 * (`POST /platform/waitlist/answers`), so a page refresh in the middle of the
 * wizard used to throw everything away and restart at 1/4.  The wizard now
 * writes the current step and the answers here on every change and reads
 * them back on mount, so a refresh lands on the same step with the earlier
 * choices still selected and editable through Back.
 *
 * Stored in localStorage (per browser) and keyed by the signed-in email so
 * two Google accounts sharing a machine never see each other's answers.  The
 * entry is removed once Done succeeds; while it exists the answers are
 * re-validated against the current option labels on every read, so a stale
 * draft from before a wording change cannot put an unknown value in a tile.
 */
const STORAGE_KEY = 'specificai.waitlistDraft.v1';

const FALLBACK_KEY = 'anonymous';

const OPTIONS_BY_KEY = Object.fromEntries(
    WAITLIST_QUESTIONS.map(({ key, options }) => [key, new Set(options)]),
);

/** Registry key for a signed-in user: the lower-cased email, `anonymous` without one. */
export const waitlistDraftKey = (user) => {
    const email = String(user?.email || '')
        .trim()
        .toLowerCase();
    return email || FALLBACK_KEY;
};

const storage = () => {
    try {
        return globalThis.localStorage || null;
    } catch {
        return null;
    }
};

const readRegistry = () => {
    const store = storage();
    if (!store) return {};
    try {
        const parsed = JSON.parse(store.getItem(STORAGE_KEY));
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
};

const writeRegistry = (registry) => {
    const store = storage();
    if (!store) return;
    try {
        if (Object.keys(registry).length === 0) {
            store.removeItem(STORAGE_KEY);
        } else {
            store.setItem(STORAGE_KEY, JSON.stringify(registry));
        }
    } catch {
        // Storage unavailable (private mode / quota) — the draft just isn't remembered.
    }
};

/** Keep only answers that are still one of the question's option labels. */
const sanitizeAnswers = (answers) => {
    const clean = emptyWaitlistAnswers();
    if (!answers || typeof answers !== 'object') return clean;
    for (const key of Object.keys(clean)) {
        const value = answers[key];
        if (typeof value === 'string' && OPTIONS_BY_KEY[key].has(value)) clean[key] = value;
    }
    return clean;
};

const sanitizeStep = (step) => {
    const index = Number(step);
    if (!Number.isInteger(index)) return 0;
    return Math.min(Math.max(index, 0), WAITLIST_STEP_COUNT - 1);
};

/**
 * The saved `{ step, answers }` for `key`, or `null` when nothing was saved.
 * Both fields are sanitised: the step is clamped to the wizard's range and
 * unknown answer values are dropped (`null` = unanswered).
 */
export const loadWaitlistDraft = (key) => {
    if (!key) return null;
    const entry = readRegistry()[key];
    if (!entry || typeof entry !== 'object') return null;
    return { step: sanitizeStep(entry.step), answers: sanitizeAnswers(entry.answers) };
};

/** Record the step the applicant is on and every answer picked so far. */
export const saveWaitlistDraft = (key, { step, answers }) => {
    if (!key) return;
    const registry = readRegistry();
    registry[key] = { step: sanitizeStep(step), answers: sanitizeAnswers(answers), updatedAt: Date.now() };
    writeRegistry(registry);
};

/** Forget the draft (Done succeeded, or the request no longer needs answers). */
export const clearWaitlistDraft = (key) => {
    if (!key) return;
    const registry = readRegistry();
    if (!(key in registry)) return;
    delete registry[key];
    writeRegistry(registry);
};
